import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ConfigContext } from '../../Config/ConfigContext';
import EnhancedTable from '../../EnhancedTable';
import StatusMessage from '../../StatusMessage';
import { Box } from '@mui/system';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Typography
} from '@mui/material';
import { isEmpty } from 'lodash';
import useApi from '../../hooks/useApi';

/**
 * Submits the new email template form data.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.values - The form values.
 * @param {Function} params.onFail - Callback for failed submission.
 * @param {Function} params.onInvalidForm - Callback for invalid form errors.
 * @param {Function} params.onChangeBulk - Callback to update state in bulk.
 * @param {Function} params.onResetFormErrors - Callback to reset form errors.
 * @param {Object} params.api - The API instance from useApi.
 * @returns {Promise<void>} Returns a promise that resolves when submission is complete.
 */
const addEmailTemplateSubmit = async ({
	values,
	onFail,
	onInvalidForm,
	onChangeBulk,
	onResetFormErrors,
	api,
}) => {
	onChangeBulk({
		statusType: '',
		statusMessage: ''
	});

	let errors = {};

	if (!values.templateName) errors.templateName = 'Template Name is required';
	if (!values.subject) errors.subject = 'Subject is required';
	if (!values.from) errors.from = 'From Address is required';
	if (!isEmpty(errors)) return onInvalidForm(errors);

	onResetFormErrors();

	try {
		const result = await api.emailTemplates.create({
			data: {
				name: values.templateName,
				subject: values.subject,
				from: values.from,
			}
		});
		onChangeBulk({
			templateName: '',
			subject: '',
			from: '',
			modalToggle: false,
			statusType: 'success',
			statusMessage: `Successfully saved new Email Template: ${values.templateName}`,
			_id: result._id
		});
	} catch (error) {
		onFail({ message: error.message });
	}
};

/**
 * EmailTemplates component provides a user interface for adding and viewing email templates.
 *
 * @component
 * @example
 * return (
 *   <EmailTemplates />
 * )
 */
export default function EmailTemplates() {
	const navigate = useNavigate();
	const { config } = useContext(ConfigContext);
	const api = useApi();

	const [values, setValues] = useState({
		modalToggle: false,
		config,
		templateName: '',
		subject: '',
		from: '',
		statusType: '',
		statusMessage: ''
	});
	const [errors, setErrors] = useState({});

	const onChange = (value, field) => {
		setValues(prev => ({ ...prev, [field]: value }));
	};

	/**
	 * Callback for invalid form submission.
	 *
	 * @param {Object} errs - The errors object.
	 */
	const onInvalidForm = (errs) => {
		setErrors(errs);
	};

	/**
	 * Callback for failed email template submission.
	 *
	 * @param {Object} err - The error object.
	 */
	const onFail = (err) => {
		setValues(prev => ({
			...prev,
			statusMessage: err.message || 'Error occurred',
			statusType: 'error'
		}));
	};

	/**
	 * Callback to update state in bulk.
	 *
	 * @param {Object} changes - The changes to be merged into state.
	 */
	const onChangeBulk = (changes) => {
		setValues(prev => ({ ...prev, ...changes }));
	};

	/**
	 * Resets the form errors.
	 */
	const onResetFormErrors = () => {
		setErrors({});
	};

	/**
	 * Intended to refresh data (currently a placeholder).
	 */
	const onRefresh = () => {
		// No operation defined
	};

	/**
	 * Closes the modal for adding an email template.
	 */
	const handleCloseModal = () => {
		setValues(prev => ({ ...prev, modalToggle: false }));
	};

	/**
	 * Opens the modal for adding an email template.
	 */
	const handleOpenModal = () => {
		setValues(prev => ({ ...prev, modalToggle: true }));
	};

	return (
		<div>
			{values.statusType &&
				<StatusMessage
					messageType={values.statusType}
					message={values.statusMessage}
				/>
			}

			{values.modalToggle &&
				<Dialog
					open={values.modalToggle}
					onClose={handleCloseModal}
					fullWidth
					maxWidth="sm"
				>
					<DialogTitle>Add Email Template</DialogTitle>
					<DialogContent>
						<Typography variant="subtitle1" gutterBottom>
							Fill out this form to add a new email template to be used by the platform
						</Typography>
						<Box
							sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
						>
							<TextField
								label="Template Name"
								value={values.templateName}
								onChange={(e) => onChange(e.target.value, 'templateName')}
								error={Boolean(errors.templateName)}
								helperText={errors.templateName}
								fullWidth
							/>
							<TextField
								label="Subject"
								value={values.subject}
								onChange={(e) => onChange(e.target.value, 'subject')}
								error={Boolean(errors.subject)}
								helperText={errors.subject}
								fullWidth
							/>
							<TextField
								label="From Address"
								value={values.from}
								onChange={(e) => onChange(e.target.value, 'from')}
								error={Boolean(errors.from)}
								helperText={errors.from}
								fullWidth
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseModal}>Cancel</Button>
						<Button
							variant="contained"
							onClick={() => addEmailTemplateSubmit({
								values,
								onInvalidForm,
								onFail,
								onChangeBulk,
								onResetFormErrors,
								api
							})}
						>
							Submit
						</Button>
					</DialogActions>
				</Dialog>
			}

			<Box
				sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
			>
				<Button
					variant="contained"
					onClick={handleOpenModal}
				>
					Add Email Template
				</Button>
			</Box>
			<EnhancedTable
				tableName={'Email Templates'}
				searchEndpoint={'/email/template'}
				actionRowClick={({ rowData }) => navigate(`/admin/email/template/${rowData._id}`)}
				headCells={[
					{
						id: 'name',
						numeric: false,
						disablePadding: true,
						label: 'Template Name'
					},
					{
						id: 'subject',
						numeric: false,
						disablePadding: true,
						label: 'Subject'
					},
					{
						id: 'from',
						numeric: false,
						disablePadding: true,
						label: 'From Address'
					},
					{
						id: 'updatedAt',
						numeric: false,
						disablePadding: true,
						label: 'Updated At'
					},
					{
						id: 'insertedAt',
						numeric: false,
						disablePadding: true,
						label: 'Created At'
					}
				]}
				filterList={[
					{
						id: 'name',
						dataType: 'string',
						label: 'Application Name',
						defaultValue: ''
					}
				]}
			/>
		</div>
	);
}

EmailTemplates.propTypes = {};
